import React, { useCallback, useEffect, useState } from "react";
import { CircleNotch, ArrowsClockwise, X, CheckCircle, MinusCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FYS = ["2023-24", "2024-25", "2025-26", "2026-27"];

export default function BulkRoll({ onClose, financialYears }) {
  const fys = financialYears?.length ? [...financialYears, "2026-27"] : FYS;
  const [sourceFy, setSourceFy] = useState("2024-25");
  const [targetFy, setTargetFy] = useState("2025-26");
  const [dir, setDir] = useState("forward");
  const [defGrowth, setDefGrowth] = useState(10);
  const [overwrite, setOverwrite] = useState(false);
  const [rows, setRows] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get(`/tax/statements/roll-candidates?source_fy=${sourceFy}&target_fy=${targetFy}`); setRows(r.data.clients); setResults(null); }
    catch { toast.error("Could not load clients"); }
  }, [sourceFy, targetFy]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    const eligible = rows.filter((r) => r.has_source);
    if (!eligible.length) { toast.error(`No clients have a ${sourceFy} statement to roll`); return; }
    setBusy(true);
    try {
      const r = await api.post("/tax/statements/bulk-roll", {
        source_fy: sourceFy, target_fy: targetFy, direction: dir,
        default_growth_pct: Number(defGrowth), overrides: Object.fromEntries(Object.entries(overrides).map(([k, v]) => [k, Number(v)])),
        overwrite,
      });
      setResults(r.data);
      toast.success(`Rolled ${r.data.rolled} of ${r.data.total_candidates} client(s) to ${targetFy}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk roll failed"); } finally { setBusy(false); }
  };

  const eligibleCount = rows.filter((r) => r.has_source).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="bulk-roll-modal">
      <div className="bg-[#FCFAF8] rounded-2xl w-full max-w-3xl max-h-[88vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <div><p className="font-display text-lg font-semibold flex items-center gap-2"><ArrowsClockwise size={20} weight="duotone" className="text-[#F29A86]" /> Bulk Roll Financials</p>
            <p className="text-xs text-[#8C8381] mt-0.5">Roll every client's statement to another F.Y. in one action, with an optional per-client growth %.</p></div>
          <button data-testid="bulk-roll-close" onClick={onClose} className="text-[#8C8381] hover:text-[#2A2625]"><X size={20} /></button>
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div><label className="text-xs text-[#8C8381]">Source F.Y.</label><select data-testid="bulk-source-fy" value={sourceFy} onChange={(e) => setSourceFy(e.target.value)} className="mt-1 block bg-white border border-[#2A2625]/15 rounded-lg px-2 py-2 text-sm">{fys.map((f) => <option key={f}>{f}</option>)}</select></div>
          <div><label className="text-xs text-[#8C8381]">Target F.Y.</label><select data-testid="bulk-target-fy" value={targetFy} onChange={(e) => setTargetFy(e.target.value)} className="mt-1 block bg-white border border-[#2A2625]/15 rounded-lg px-2 py-2 text-sm">{fys.map((f) => <option key={f}>{f}</option>)}</select></div>
          <div><label className="text-xs text-[#8C8381]">Direction</label><select value={dir} onChange={(e) => setDir(e.target.value)} className="mt-1 block bg-white border border-[#2A2625]/15 rounded-lg px-2 py-2 text-sm"><option value="forward">Forward (+%)</option><option value="backward">Backward (−%)</option></select></div>
          <div><label className="text-xs text-[#8C8381]">Default growth %</label><input data-testid="bulk-default-growth" type="number" value={defGrowth} onChange={(e) => setDefGrowth(e.target.value)} className="mt-1 block w-24 bg-white border border-[#2A2625]/15 rounded-lg px-2 py-2 text-sm" /></div>
          <label className="flex items-center gap-2 text-xs text-[#594F4D]"><input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="bulk-overwrite" /> Overwrite existing {targetFy}</label>
        </div>

        <div className="bg-white border border-[#2A2625]/10 rounded-xl overflow-hidden mb-4">
          <div className="flex items-center justify-between px-3 py-2 bg-[#FFF4F0] text-xs text-[#594F4D]">
            <span>{rows.length} clients · {eligibleCount} have a {sourceFy} statement</span>
            <span>Per-client growth % (blank = default {defGrowth}%)</span>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {rows.map((c) => (
              <div key={c.client_id} className={`flex items-center justify-between px-3 py-1.5 border-t border-[#2A2625]/6 text-sm ${!c.has_source ? "opacity-40" : ""}`} data-testid={`bulk-row-${c.client_id}`}>
                <span className="flex items-center gap-2">{c.firm_name} <span className="text-[10px] text-[#8C8381]">{c.business_type}</span>{c.has_target && <span className="text-[10px] text-[#c0392b]">· {targetFy} exists</span>}</span>
                <input type="number" placeholder={`${defGrowth}`} disabled={!c.has_source} value={overrides[c.client_id] ?? ""} onChange={(e) => setOverrides((o) => ({ ...o, [c.client_id]: e.target.value }))} className="w-20 text-right bg-[#FCFAF8] border border-[#2A2625]/12 rounded-lg px-2 py-1 text-xs outline-none" />
              </div>
            ))}
            {!rows.length && <p className="text-sm text-[#8C8381] px-3 py-4">No clients yet.</p>}
          </div>
        </div>

        {results && (
          <div className="bg-white border border-[#2A2625]/10 rounded-xl p-3 mb-4 max-h-40 overflow-y-auto" data-testid="bulk-results">
            <p className="text-sm font-semibold mb-2">Rolled {results.rolled} of {results.total_candidates}</p>
            {results.results.map((r) => (
              <div key={r.client_id} className="flex items-center justify-between text-xs py-0.5">
                <span className="flex items-center gap-1">{r.status === "rolled" ? <CheckCircle size={13} weight="fill" className="text-[#A3C2A4]" /> : <MinusCircle size={13} className="text-[#8C8381]" />}{r.firm_name}</span>
                <span className="text-[#8C8381]">{r.status === "rolled" ? `×${r.factor} · NP ₹${Number(r.net_profit || 0).toLocaleString("en-IN")}` : r.reason}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-full border border-[#2A2625]/15">Close</button>
          <button data-testid="bulk-run" onClick={run} disabled={busy || !eligibleCount} className="flex items-center gap-2 bg-[#2A2625] text-white font-medium rounded-full px-5 py-2 text-sm disabled:opacity-40">{busy ? <CircleNotch size={15} className="animate-spin" /> : <ArrowsClockwise size={15} />} Roll {eligibleCount} client(s)</button>
        </div>
      </div>
    </div>
  );
}
